/**
 * SubmittalsTab
 * -------------
 * Numbered submittal log with ball-in-court, review due dates, revision
 * history, attachments and a printable transmittal.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Download, Send, Trash2, Paperclip, Upload, ClipboardList, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API } from "@/App";

const TYPES = [
  { value: "product_data", label: "Product data" },
  { value: "shop_drawing", label: "Shop drawing" },
  { value: "sample", label: "Physical sample" },
  { value: "mock_up", label: "Mock-up" },
  { value: "coordination_drawing", label: "Coordination drawing" },
  { value: "test_report", label: "Test / certification report" },
  { value: "om_manual", label: "O&M manual" },
  { value: "warranty", label: "Warranty" },
  { value: "closeout", label: "Closeout document" },
];

const DISPOSITIONS = [
  { value: "in_review", label: "Mark in review" },
  { value: "approved", label: "Approved" },
  { value: "approved_as_noted", label: "Approved as noted" },
  { value: "revise_and_resubmit", label: "Revise and resubmit" },
  { value: "rejected", label: "Rejected" },
  { value: "closed", label: "Close out" },
];

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  submitted: "bg-amber-50 text-amber-700 ring-amber-300",
  in_review: "bg-blue-50 text-blue-700 ring-blue-300",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  approved_as_noted: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  revise_and_resubmit: "bg-red-50 text-red-700 ring-red-300",
  rejected: "bg-red-50 text-red-700 ring-red-300",
  closed: "bg-slate-100 text-slate-500 ring-slate-300",
};

const blank = () => ({
  title: "", spec_section: "", submittal_type: "product_data", division_code: "",
  description: "", responsible_company: "", contractor_email: "", reviewer_name: "",
  date_required: "", review_due_date: "", lead_time_days: "",
});

const Stat = ({ label, value, accent = "text-slate-900", sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`text-lg font-black mt-1 ${accent}`}>{value}</p>
    {sub ? <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p> : null}
  </div>
);

export default function SubmittalsTab({ projectId, project, token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [review, setReview] = useState({ status: "approved", reviewer_notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/submittals?project_id=${projectId}`, { headers });
      setRows(r.data?.submittals || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the submittal log");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the submittal a title");
    setSaving(true);
    try {
      const body = { ...form, lead_time_days: form.lead_time_days ? Number(form.lead_time_days) : null };
      const r = editingId
        ? await axios.put(`${API}/submittals/${editingId}`, body, { headers })
        : await axios.post(`${API}/submittals`, { project_id: projectId, ...body }, { headers });
      toast.success(`${r.data.submittal.number} saved`);
      setForm(null); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the submittal");
    } finally { setSaving(false); }
  };

  const submit = async (row) => {
    try {
      const r = await axios.post(`${API}/submittals/${row.id}/submit`, {}, { headers });
      toast.success(`${row.number} submitted for review`, {
        description: `Revision ${r.data.submittal.revision}`,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    }
  };

  const disposition = async () => {
    try {
      await axios.post(`${API}/submittals/${reviewing.id}/disposition`, review, { headers });
      toast.success(`${reviewing.number} → ${review.status.replace(/_/g, " ")}`);
      setReviewing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the review");
    }
  };

  const upload = async (row, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/submittals/${row.id}/attachments`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} attached`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.number}?`)) return;
    try {
      await axios.delete(`${API}/submittals/${row.id}`, { headers });
      toast.success(`${row.number} deleted`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const openPdf = async (row) => {
    try {
      const r = await axios.get(`${API}/submittals/${row.id}/pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${row.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the transmittal");
    }
  };

  if (loading) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-8">
      <Loader2 className="w-4 h-4 animate-spin" />Loading submittal log…</p>;
  }

  return (
    <div className="space-y-5" data-testid="submittals-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Submittal Log</h2>
          <p className="text-sm text-slate-500">
            Shop drawings, product data and closeout documents — numbered, dated and tracked to a reviewer.
          </p>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => { setEditingId(null); setForm({ ...blank(), reviewer_name: project?.pm_name || "" }); }}
          data-testid="new-submittal-btn">
          <Plus className="w-4 h-4 mr-2" />New Submittal
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="submittal-summary">
          <Stat label="Total submittals" value={summary.total} />
          <Stat label="Open" value={summary.open} accent="text-amber-600"
            sub={`${summary.ball_with_pm} with the reviewer`} />
          <Stat label="Overdue" value={summary.overdue}
            accent={summary.overdue ? "text-red-600" : "text-slate-900"} sub="Past review due date" />
          <Stat label="Ball with contractor" value={summary.ball_with_contractor} accent="text-blue-700" />
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 bg-white"
          data-testid="submittals-empty">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">No submittals logged yet</p>
          <p className="text-sm text-slate-400 mt-1">Log what each trade owes you, then stamp it when it comes back.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
          <table className="w-full min-w-[1040px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["No.", "Title / spec", "Type", "Responsible", "Review due", "Ball in court", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/70" data-testid={`submittal-row-${row.number}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-bold text-blue-700">{row.number}</span>
                    {row.revision > 0 ? <span className="ml-1 text-[10px] font-bold text-slate-400">R{row.revision}</span> : null}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{row.title}</p>
                    <p className="text-xs text-slate-500">
                      {row.spec_section || "—"}{row.division_code ? ` · ${row.division_code}` : ""}
                      {(row.attachments || []).length ? ` · ${row.attachments.length} file(s)` : ""}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {TYPES.find((t) => t.value === row.submittal_type)?.label || row.submittal_type}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{row.responsible_company || "—"}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={row.is_overdue ? "text-red-600 font-bold inline-flex items-center gap-1" : "text-slate-600"}>
                      {row.is_overdue ? <AlertTriangle className="w-3.5 h-3.5" /> : null}
                      {row.review_due_date || row.date_required || "—"}
                    </span>
                    {row.is_open && row.days_until_due !== null && !row.is_overdue ? (
                      <span className="block text-[11px] text-slate-400">in {row.days_until_due} days</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{row.ball_in_court}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_STYLE[row.status]}`}>
                      {(row.status || "").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {["draft", "revise_and_resubmit", "rejected"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50"
                          onClick={() => submit(row)} data-testid={`submittal-submit-${row.number}`}>
                          <Send className="w-3.5 h-3.5 mr-1" />
                          {row.status === "draft" ? "Submit" : "Resubmit"}
                        </Button>
                      )}
                      {["submitted", "in_review"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => { setReviewing(row); setReview({ status: "approved", reviewer_notes: "" }); }}
                          data-testid={`submittal-review-${row.number}`}>
                          Review
                        </Button>
                      )}
                      <label className="inline-flex items-center px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 cursor-pointer"
                        title="Attach a file" data-testid={`submittal-attach-${row.number}`}>
                        <Upload className="w-3.5 h-3.5" />
                        <input type="file" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(row, f); e.target.value = ""; }} />
                      </label>
                      {(row.attachments || []).length > 0 && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${row.attachments[row.attachments.length - 1].url}`}
                          target="_blank" rel="noreferrer"
                          className="px-2 py-1 text-slate-500 hover:text-slate-800" title="Open latest attachment">
                          <Paperclip className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                        onClick={() => { setEditingId(row.id); setForm({ ...blank(), ...row }); }}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                        onClick={() => openPdf(row)} data-testid={`submittal-pdf-${row.number}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                        onClick={() => remove(row)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit submittal" : "Log a submittal"}</DialogTitle>
            <DialogDescription>What the trade owes, who reviews it and when it is needed on site.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Title</label>
                <Input value={form.title} placeholder="Isolation room door hardware — product data"
                  onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="submittal-title-input" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Spec section</label>
                <Input value={form.spec_section} placeholder="08 71 00"
                  onChange={(e) => setForm({ ...form, spec_section: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Type</label>
                <Select value={form.submittal_type} onValueChange={(v) => setForm({ ...form, submittal_type: v })}>
                  <SelectTrigger data-testid="submittal-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Division</label>
                <Input value={form.division_code} placeholder="DIV 08 · Openings"
                  onChange={(e) => setForm({ ...form, division_code: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Responsible company</label>
                <Input value={form.responsible_company} placeholder="Trade partner"
                  onChange={(e) => setForm({ ...form, responsible_company: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Contractor portal email</label>
                <Input value={form.contractor_email} placeholder="foreman@trade.com"
                  onChange={(e) => setForm({ ...form, contractor_email: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Reviewer</label>
                <Input value={form.reviewer_name}
                  onChange={(e) => setForm({ ...form, reviewer_name: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Review due</label>
                <Input type="date" value={form.review_due_date || ""}
                  onChange={(e) => setForm({ ...form, review_due_date: e.target.value })}
                  data-testid="submittal-review-due-input" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Needed on site</label>
                <Input type="date" value={form.date_required || ""}
                  onChange={(e) => setForm({ ...form, date_required: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Description</label>
                <Textarea rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}
              data-testid="submittal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save submittal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review {reviewing?.number}</DialogTitle>
            <DialogDescription>
              {reviewing?.title} — revision {reviewing?.revision}. Revise-and-resubmit sends it back to the trade.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Disposition</label>
              <Select value={review.status} onValueChange={(v) => setReview({ ...review, status: v })}>
                <SelectTrigger data-testid="submittal-disposition-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISPOSITIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Reviewer notes</label>
              <Textarea rows={4} value={review.reviewer_notes}
                placeholder="Confirm the hardware finish matches the door schedule before fabrication."
                onChange={(e) => setReview({ ...review, reviewer_notes: e.target.value })}
                data-testid="submittal-reviewer-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={disposition}
              data-testid="submittal-disposition-btn">Record review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
